/**
 * Production domain types.
 *
 * The production system sits between Orders (sales) and Inventory (materials):
 *
 *   Order (scheduled) ─┬→  WorkTicket ──→  Station routing (sequential)
 *                      │                    ├─ queued
 *                      │                    ├─ in_progress
 *                      │                    ├─ blocked (hold / waiting)
 *                      │                    └─ complete
 *                      │
 *                      └→  Production Schedule (what's due when)
 *
 *   Each ticket records:
 *     • station visits with operator + duration (labor log)
 *     • material consumptions (→ writes to inventory movements)
 *     • QC checkpoints (pass / fail / rework)
 *     • scrap events
 *
 * Shop floor = ticket board. Each station sees its queued tickets.
 */

// ─────────── Stations ───────────

export type Station =
  | 'veneer_prep'
  | 'panel_line'
  | 'door_line'
  | 'finish_line'
  | 'assembly'
  | 'qc'
  | 'packaging'
  | 'shipping';

export const STATIONS: Station[] = [
  'veneer_prep', 'panel_line', 'door_line', 'finish_line',
  'assembly', 'qc', 'packaging', 'shipping',
];

export const STATION_LABELS: Record<Station, string> = {
  veneer_prep:  'Veneer Prep',
  panel_line:   'Panel Line',
  door_line:    'Door Line',
  finish_line:  'Finish · UV',
  assembly:     'Assembly',
  qc:           'Quality Control',
  packaging:    'Packaging',
  shipping:     'Shipping',
};

// ─────────── Ticket lifecycle ───────────

export type TicketStatus =
  | 'draft'            // created but not released to floor
  | 'queued'           // on the board at its current station
  | 'in_progress'      // operator has claimed and started
  | 'blocked'          // on hold (waiting on material, approval, rework)
  | 'complete'         // all stations done, ready to ship
  | 'cancelled';

export const TICKET_STATUSES: TicketStatus[] = [
  'draft', 'queued', 'in_progress', 'blocked', 'complete', 'cancelled',
];

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  draft:       'Draft',
  queued:      'Queued',
  in_progress: 'In Progress',
  blocked:     'Blocked',
  complete:    'Complete',
  cancelled:   'Cancelled',
};

export type Priority = 'low' | 'normal' | 'high' | 'rush';

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: 'Low', normal: 'Normal', high: 'High', rush: 'Rush',
};

// ─────────── Station routing ───────────

export type RoutingStep = {
  station: Station;
  estimatedMinutes?: number;
  completed: boolean;
  completedAt?: string;
  completedBy?: string;
  completedByName?: string;
  notes?: string;
};

// ─────────── Labor log ───────────

export type LaborEvent = {
  id: string;
  station: Station;
  operatorUid: string;
  operatorName?: string;
  startedAt: string;
  endedAt?: string;
  minutes?: number;     // derived when ended
  notes?: string;
};

// ─────────── Material consumption ───────────

export type MaterialConsumption = {
  id: string;
  itemId: string;
  itemSku?: string;
  itemName?: string;
  quantity: number;
  unit?: string;
  consumedAt: string;
  consumedBy?: string;
  /** Id of the linked inventory movement */
  movementId?: string;
};

// ─────────── QC ───────────

export type QCOutcome = 'pass' | 'fail' | 'rework';

export const QC_OUTCOME_LABELS: Record<QCOutcome, string> = {
  pass: 'Pass', fail: 'Fail', rework: 'Rework',
};

export type QCCheck = {
  id: string;
  checkedAt: string;
  checkedBy: string;
  checkedByName?: string;
  outcome: QCOutcome;
  /** Optional checklist results */
  items?: Array<{ label: string; pass: boolean; note?: string }>;
  defectCategory?: string;     // for fail/rework
  reworkStation?: Station;     // which station to send back to
  notes?: string;
};

// ─────────── Work ticket ───────────

export type WorkTicket = {
  id: string;
  number: string;              // 'WT-2601-A' — derived from order
  status: TicketStatus;
  priority: Priority;

  // Source linkage
  orderId: string;
  orderNumber?: string;
  lineItemId?: string;         // which line of the order this ticket is for
  clientId?: string;
  clientName?: string;
  projectName?: string;

  // Product description (denormalized)
  description: string;
  quantity: number;
  unit?: string;
  specs?: {
    veneerSpecies?: string;
    cut?: string;
    finish?: string;
    dimensions?: string;
    grade?: string;
  };

  // Schedule
  releasedAt?: string;         // when moved from draft → queued
  startedAt?: string;          // when first operator claimed
  completedAt?: string;
  dueDate?: string;             // inherit from order's promised date

  // Routing — ordered list of stations for this ticket
  routing: RoutingStep[];
  /** Index into routing[] of the current active step. */
  currentStepIndex: number;

  // Logs
  labor: LaborEvent[];
  consumptions: MaterialConsumption[];
  qcChecks: QCCheck[];

  // Aggregates (filled by backend on read)
  totalLaborMinutes?: number;
  totalMaterialCost?: number;

  notes?: string;

  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
};

// ─────────── Helpers ───────────

/** The current station for a ticket — undefined if done */
export function currentStation(ticket: WorkTicket): Station | undefined {
  return ticket.routing[ticket.currentStepIndex]?.station;
}

/** Progress through routing as a percentage */
export function routingProgress(ticket: WorkTicket): number {
  if (ticket.routing.length === 0) return 0;
  const done = ticket.routing.filter((r) => r.completed).length;
  return (done / ticket.routing.length) * 100;
}

/** Default routing by ticket kind — pragmatic starting point */
export function defaultRoutingFor(productKind: string): Station[] {
  switch (productKind) {
    case 'veneer':
      return ['veneer_prep', 'qc', 'packaging', 'shipping'];
    case 'panel':
      return ['veneer_prep', 'panel_line', 'finish_line', 'qc', 'packaging', 'shipping'];
    case 'door':
      return ['veneer_prep', 'door_line', 'finish_line', 'qc', 'packaging', 'shipping'];
    case 'jamb':
      return ['door_line', 'finish_line', 'qc', 'packaging', 'shipping'];
    case 'finish':
      return ['finish_line', 'qc', 'packaging', 'shipping'];
    default:
      return ['panel_line', 'qc', 'packaging', 'shipping'];
  }
}
